import React, { useState } from 'react';
import { Dropdown, Button, Space, Typography, Modal, Form, Input, message } from 'antd';
import { UserOutlined, LogoutOutlined, KeyOutlined, DownOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const { Text } = Typography;

const UserMenu = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [passwordForm] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const showPasswordModal = () => {
    setPasswordModalVisible(true);
    passwordForm.resetFields();
  };

  const handlePasswordCancel = () => {
    setPasswordModalVisible(false);
    passwordForm.resetFields();
  };

  const handlePasswordChange = async (values) => {
    setLoading(true);
    try {
      await axios.patch('/users/me/change-password', {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword
      });
      message.success(t('auth.passwordChangeSuccess'));
      handlePasswordCancel();
    } catch (err) {
      console.error('Error changing password:', err);
      message.error(err.response?.data?.message || t('auth.passwordChangeFailed'));
    } finally {
      setLoading(false);
    }
  };

  const menuItems = [
    {
      key: 'change-password',
      icon: <KeyOutlined />,
      label: t('navigation.changePassword'),
      onClick: showPasswordModal
    },
    {
      type: 'divider'
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('navigation.logout'),
      onClick: handleLogout
    }
  ];

  return (
    <>
      <Dropdown
        menu={{ items: menuItems }}
        trigger={['click']}
        placement="bottomRight"
      >
        <Button type="text" style={{ height: 'auto', padding: '4px 8px' }}>
          <Space>
            <UserOutlined />
            <Text>{user?.name || 'User'}</Text>
            <DownOutlined style={{ fontSize: '12px' }} />
          </Space>
        </Button>
      </Dropdown>

      {/* Password Change Modal */}
      <Modal
        title={t('navigation.changePassword')}
        open={passwordModalVisible}
        onCancel={handlePasswordCancel}
        footer={null}
        width={400}
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handlePasswordChange}
          autoComplete="off"
        >
          <Form.Item
            name="currentPassword"
            label={t('auth.currentPassword')}
            rules={[
              { required: true, message: t('auth.pleaseInputPassword') }
            ]}
          >
            <Input.Password placeholder={t('auth.enterCurrentPassword')} />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label={t('auth.newPassword')}
            rules={[
              { required: true, message: t('auth.pleaseInputPassword') },
              { min: 8, message: t('auth.passwordMinLength') },
              {
                pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                message: t('auth.passwordRequirements')
              }
            ]}
          >
            <Input.Password placeholder={t('auth.enterNewPassword')} />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label={t('auth.confirmPassword')}
            dependencies={['newPassword']}
            rules={[
              { required: true, message: t('auth.pleaseInputPassword') },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t('auth.passwordsDoNotMatch')));
                }
              })
            ]}
          >
            <Input.Password placeholder={t('auth.confirmNewPassword')} />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={handlePasswordCancel}>
                {t('common.cancel')}
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {t('navigation.changePassword')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default UserMenu;
